/**
 * Motion helpers. The vocabulary itself is CSS (`motion.css`); this module
 * holds the timings, the reduced-motion preference and the view-transition
 * wrapper.
 *
 * Motion is reduced when the operating system asks for it
 * (`prefers-reduced-motion: reduce`) **or** the person turned it off in Hire.
 * The in-app setting can only reduce motion, never force it back on.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

/** Motion timings in milliseconds, matching the CSS tokens. */
export const MOTION = {
  fastMs: 150,
  baseMs: 250,
  slowMs: 400,
  emphasisMs: 600,
  staggerMs: 35,
  /** Lists stagger at most this many items; later items share the last delay. */
  maxStagger: 8,
  /** Skeletons appear only for loads longer than this. */
  skeletonDelayMs: 300,
} as const;

/**
 * The `data-stagger` value for the item at `index` (0-based), capped so a
 * long list never waits more than eight steps.
 */
export function staggerIndex(index: number): string {
  return String(Math.max(0, Math.min(Math.trunc(index), MOTION.maxStagger - 1)));
}

/** The in-app motion setting: follow the system, or always reduce. */
export type MotionPreference = 'system' | 'reduced';

const STORAGE_KEY = 'hire.motion';
const CHANGE_EVENT = 'hire:motion-change';
const QUERY = '(prefers-reduced-motion: reduce)';

/** The saved in-app setting (`system` when nothing is saved or storage is unavailable). */
export function getMotionPreference(): MotionPreference {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'reduced' ? 'reduced' : 'system';
  } catch {
    return 'system';
  }
}

/** Reflects the in-app setting on `<html data-motion>`, which `motion.css` reads. */
export function applyMotionPreference(preference: MotionPreference = getMotionPreference()): void {
  if (preference === 'reduced') document.documentElement.dataset.motion = 'reduced';
  else delete document.documentElement.dataset.motion;
}

/**
 * Saves and applies the in-app setting, and tells every `useReducedMotion` hook.
 *
 * @param preference - `reduced` turns animations off; `system` follows the OS.
 */
export function setMotionPreference(preference: MotionPreference): void {
  try {
    if (preference === 'reduced') localStorage.setItem(STORAGE_KEY, 'reduced');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage may be unavailable (private mode); the setting still applies now.
  }
  applyMotionPreference(preference);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * The in-app setting, re-read whenever `setMotionPreference` changes it, so a
 * control showing it always matches what is saved.
 */
export function useMotionPreference(): MotionPreference {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener(CHANGE_EVENT, onChange);
      return () => {
        window.removeEventListener(CHANGE_EVENT, onChange);
      };
    },
    getMotionPreference,
    () => 'system',
  );
}

/**
 * The in-app setting an account stands for on this device, or `null` to leave
 * the device alone. The account is the source of truth: one that has not asked
 * to reduce motion clears a setting an earlier account left on this device,
 * so it never starts out reduced. A test session or someone viewed as is
 * somebody else's account, so it never touches the device.
 */
export function motionForAccount(
  account:
    | {
        reducedMotion?: string | undefined;
        impersonation?: unknown;
        sandbox?: unknown;
      }
    | null
    | undefined,
): MotionPreference | null {
  if (!account?.reducedMotion || account.impersonation || account.sandbox) return null;
  return account.reducedMotion === 'reduce' ? 'reduced' : 'system';
}

/** Whether motion is reduced right now (system or in-app). */
export function isMotionReduced(): boolean {
  const system = typeof window.matchMedia === 'function' && window.matchMedia(QUERY).matches;
  return system || document.documentElement.dataset.motion === 'reduced';
}

function subscribe(onChange: () => void): () => void {
  const media = typeof window.matchMedia === 'function' ? window.matchMedia(QUERY) : undefined;
  media?.addEventListener('change', onChange);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    media?.removeEventListener('change', onChange);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
}

/**
 * Whether motion should be reduced: the system preference combined with the
 * in-app setting. Re-renders when either changes.
 */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, isMotionReduced, () => false);
}

/** Whether the launch between Gradex Profile and Gradex Tools is playing (it marks `<html>`). */
export function isAreaLaunching(): boolean {
  return document.documentElement.dataset.areaLaunch === 'on';
}

interface ViewTransitionDocument {
  startViewTransition?: (update: () => void | Promise<void>) => {
    updateCallbackDone: Promise<void>;
  };
}

/**
 * Runs a DOM update inside a view transition (the page cross-fade in
 * `motion.css`) when the browser supports it and motion is not reduced;
 * otherwise runs it directly. While the launch between Gradex Profile and
 * Gradex Tools plays, that is the transition, so this runs the update directly.
 *
 * @param update - The change to make.
 * @returns Resolves once the update has run.
 */
export async function viewTransition(update: () => void | Promise<void>): Promise<void> {
  const start = (document as ViewTransitionDocument).startViewTransition;
  // Moving between Gradex Profile and Gradex Tools has its own animation (`area-launch.ts`).
  if (typeof start !== 'function' || isMotionReduced() || isAreaLaunching()) {
    await update();
    return;
  }
  await start.call(document, update).updateCallbackDone;
}

/**
 * True only after `active` has stayed true for `delayMs`: skeletons use it
 * so quick loads never flash a placeholder.
 */
export function useDelayedFlag(active: boolean, delayMs: number = MOTION.skeletonDelayMs): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!active) return undefined;
    const timer = setTimeout(() => {
      setShown(true);
    }, delayMs);
    return () => {
      clearTimeout(timer);
      setShown(false);
    };
  }, [active, delayMs]);
  return active && shown;
}

/**
 * Whether `value` has changed since the first render: components animate a
 * change of state, never their first appearance. Once true, it stays true.
 */
export function useHasChanged(value: unknown): boolean {
  const [state, setState] = useState({ initial: value, changed: false });
  const differs = !Object.is(state.initial, value);
  if (differs && !state.changed) setState({ initial: state.initial, changed: true });
  return state.changed || differs;
}

/**
 * Counts from the previous value to `target` over `durationMs` (ease-out),
 * or jumps straight there when motion is reduced.
 *
 * @returns The number to show now.
 */
export function useCountUp(target: number, durationMs: number = MOTION.emphasisMs): number {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    if (reduced) {
      from.current = target;
      return undefined;
    }
    const start = performance.now();
    const origin = from.current;
    let frame = 0;
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - (1 - progress) ** 3;
      const value = origin + (target - origin) * eased;
      setShown(value);
      from.current = value;
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [target, durationMs, reduced]);
  return reduced ? target : shown;
}
